/**
 * GeoJSON cell ChartSource — S-57 cells converted to per-cell GeoJSON by
 * scripts/ingest_enc.py and served from a base URL. The RYA training cells
 * live under public/charts/; real-world NOAA ENC cells (same S-57 -> GeoJSON
 * pipeline) live under public/charts/noaa/. Both are the SAME adapter, just a
 * different base URL, so adding NOAA needs no renderer or feature-model change.
 */
import type {
  CellMeta,
  ChartFeatureCollection,
  ChartSource,
  ChartSourceKind,
} from '@/types';

interface ManifestEntry extends CellMeta {
  /** Number of features in the cell, useful for the data-quality panel. */
  featureCount?: number;
}

interface Manifest {
  cells: ManifestEntry[];
}

/**
 * How many parsed cells to hold onto after they stop being resident on the map.
 * Re-fetching a cell you have just panned away from is wasteful, but an
 * unbounded cache is worse: the NOAA set is 7,133 cells and a single band-3 one
 * is 25 MB of GeoJSON, so a cache that only ever grows will eat the device.
 */
const DEFAULT_MAX_CACHED = 24;

/** The Vite `public/charts/` mount (BASE-aware). */
function chartsBase(): string {
  return `${import.meta.env.BASE_URL ?? '/'}charts/`;
}

export class TrainingChartSource implements ChartSource {
  readonly id: string;
  readonly name: string;
  readonly kind: ChartSourceKind = 'training';

  private readonly baseUrl: string;
  private readonly maxCached: number;
  /**
   * Where the cell list lives. Defaults to manifest.json beside the cells, which
   * is what the ingest writes. A downloadable pack names its own file instead
   * (<pack>.cells.json), so the same adapter serves a bundled set and a pack.
   */
  private readonly manifestUrl: string;
  private manifestCache: Promise<Manifest> | null = null;
  /** Insertion-ordered, so the oldest entry is the first key: a plain LRU. */
  private cellCache = new Map<string, Promise<ChartFeatureCollection>>();

  constructor(
    baseUrl: string = chartsBase(),
    opts: { id?: string; name?: string; maxCached?: number; manifestUrl?: string } = {},
  ) {
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    this.id = opts.id ?? 'training';
    this.name = opts.name ?? 'RYA training cells';
    this.maxCached = opts.maxCached ?? DEFAULT_MAX_CACHED;
    this.manifestUrl = opts.manifestUrl ?? `${this.baseUrl}manifest.json`;
  }

  async listCells(): Promise<CellMeta[]> {
    const m = await this.loadManifest();
    return m.cells;
  }

  async loadCell(id: string): Promise<ChartFeatureCollection> {
    let promise = this.cellCache.get(id);
    if (promise) {
      // Touch it so the most recently used cell is last, and therefore last out.
      this.cellCache.delete(id);
      this.cellCache.set(id, promise);
      return promise;
    }
    promise = this.fetchCell(id);
    this.cellCache.set(id, promise);
    // A failed fetch must not sit in the cache pretending to be a cell, or every
    // later attempt returns the same rejection without ever retrying.
    promise.catch(() => {
      if (this.cellCache.get(id) === promise) this.cellCache.delete(id);
    });
    this.evict();
    return promise;
  }

  /** How many parsed cells are held right now. Exposed for tests and diagnostics. */
  get cachedCount(): number {
    return this.cellCache.size;
  }

  /** Drop every cached cell, e.g. when switching to a different chart set. */
  clearCache(): void {
    this.cellCache.clear();
  }

  private evict(): void {
    while (this.cellCache.size > this.maxCached) {
      const oldest = this.cellCache.keys().next();
      if (oldest.done) return;
      this.cellCache.delete(oldest.value);
    }
  }

  private async loadManifest(): Promise<Manifest> {
    if (!this.manifestCache) {
      this.manifestCache = fetch(this.manifestUrl).then(async (r) => {
        if (!r.ok) throw new Error(`manifest.json: ${r.status} ${r.statusText}`);
        return (await r.json()) as Manifest;
      });
    }
    return this.manifestCache;
  }

  private async fetchCell(id: string): Promise<ChartFeatureCollection> {
    const r = await fetch(`${this.baseUrl}${encodeURIComponent(id)}.geojson`);
    if (!r.ok) throw new Error(`cell ${id}: ${r.status} ${r.statusText}`);
    return (await r.json()) as ChartFeatureCollection;
  }
}

/** Singleton handle for the fictional RYA training cells (public/charts/). */
export const trainingChartSource = new TrainingChartSource();

/**
 * Real-world NOAA ENC cells (public/charts/noaa/), produced by running the same
 * ingest on NOAA's free S-57 cells. Empty/absent until those cells are dropped
 * in; the loader treats a missing manifest as "no NOAA cells yet" and carries on.
 */
export const noaaChartSource = new TrainingChartSource(`${chartsBase()}noaa/`, {
  id: 'noaa',
  name: 'NOAA ENC cells',
  // bundled.json, not the manifest.json beside it. See NOAA_MANIFEST_PATH in
  // providers.ts: manifest.json describes everything the ingest converted, which
  // is the whole 7,133-cell set and about 8 GB that never enters the repo. The
  // cells the app can actually reach are the committed ones.
  manifestUrl: `${chartsBase()}noaa/bundled.json`,
});
